import { useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowRight, Calendar, CheckCircle, Clock,
  MessageSquare, Play, Shield, Star, Target,
  Users, Video, Zap,
} from 'lucide-react'
import './LandingPage.css'

export default function LandingPage() {
  const navigate = useNavigate()
  const headerRef = useRef<HTMLElement>(null)

  const toggleMobileNav = () => {
    headerRef.current?.classList.toggle('nav-open')
  }

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
    headerRef.current?.classList.remove('nav-open')
  }

  return (
    <div className="landing">

      {/* ── Header ── */}
      <header className="landing-header" ref={headerRef}>
        <div className="landing-container">
          <div className="header-brand">
            <img src="/InterviewDressRehearsal_LOGO.png" alt="IDR" className="header-logo" />
            <span className="header-name">Interview Dress Rehearsal</span>
          </div>

          <nav className="header-nav">
            <a href="#how-it-works" onClick={(e) => { e.preventDefault(); scrollTo('how-it-works') }}>How It Works</a>
            <a href="#features"     onClick={(e) => { e.preventDefault(); scrollTo('features')     }}>Features</a>
            <a href="#become-interviewer" onClick={(e) => { e.preventDefault(); scrollTo('become-interviewer') }}>Become an Interviewer</a>
            <button className="btn-outline-light" onClick={() => navigate('/signin')}>Sign In</button>
            <button className="btn-cta"           onClick={() => navigate('/signup')}>Get Started</button>
          </nav>

          <button className="mobile-nav-toggle" onClick={toggleMobileNav} aria-label="Toggle navigation">
            <span /><span /><span />
          </button>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="hero">
        <div className="landing-container">
          <div className="hero-content">
            <h1 className="hero-title">
              Bomb here.<br />
              <span className="hero-highlight">Shine when it matters.</span>
            </h1>
            <p className="hero-subtitle">
              Interview Dress Rehearsal gives you a real, live mock interview tailored to your
              role — plus clear feedback you can use immediately, so you walk into the real
              interview with confidence.
            </p>
            <div className="hero-actions">
              <button className="btn-cta btn-lg" onClick={() => navigate('/signup')}>
                Schedule a Session <ArrowRight size={20} />
              </button>
              <a href="#how-it-works" className="btn-ghost btn-lg" onClick={(e) => { e.preventDefault(); scrollTo('how-it-works') }}>
                <Play size={18} /> How It Works
              </a>
            </div>
            <div className="hero-trust">
              {['Live 1-on-1 sessions', 'Role-specific prep', 'Actionable feedback'].map((t) => (
                <div key={t} className="trust-item">
                  <CheckCircle size={16} /> <span>{t}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Mock session card */}
          <div className="hero-visual">
            <div className="hero-card">
              <div className="hero-card-header">
                <div className="hero-card-dot red"    />
                <div className="hero-card-dot yellow" />
                <div className="hero-card-dot green"  />
              </div>
              <div className="hero-card-body">
                <div className="mock-session">
                  <div className="mock-avatar"><Users size={28} /></div>
                  <div className="mock-info">
                    <span className="mock-label">Mock Interview Session</span>
                    <span className="mock-role">Senior Software Engineer</span>
                  </div>
                </div>
                <div className="mock-feedback">
                  <div className="feedback-item positive">
                    <CheckCircle size={14} /> <span>Strong problem-solving approach</span>
                  </div>
                  <div className="feedback-item positive">
                    <CheckCircle size={14} /> <span>Clear communication of ideas</span>
                  </div>
                  <div className="feedback-item improve">
                    <Target size={14} /> <span>Practice edge case handling</span>
                  </div>
                </div>
                <div className="mock-badge">Feedback Ready</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Social proof ── */}
      <section className="social-proof">
        <div className="landing-container">
          <div className="proof-stats">
            <div className="proof-stat">
              <span className="proof-number">500+</span>
              <span className="proof-label">Mock Interviews Conducted</span>
            </div>
            <div className="proof-divider" />
            <div className="proof-stat">
              <span className="proof-number">95%</span>
              <span className="proof-label">Candidates Felt More Prepared</span>
            </div>
            <div className="proof-divider" />
            <div className="proof-stat">
              <span className="proof-number">4.9/5</span>
              <span className="proof-label">Average Session Rating</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="how-it-works" className="how-it-works">
        <div className="landing-container">
          <div className="section-header">
            <h2>How It Works</h2>
            <p>Three simple steps to interview confidence</p>
          </div>
          <div className="steps-grid">
            {[
              {
                n: 1, Icon: Calendar,
                title: 'Schedule Your Session',
                desc: 'Choose your target role, share your job description, and pick a time that works for you. We match you with an experienced interviewer.',
              },
              {
                n: 2, Icon: Video,
                title: 'Live Mock Interview',
                desc: 'Join a realistic 1-on-1 interview over video. Your interviewer tailors questions to your role and evaluates your performance in real time.',
              },
              {
                n: 3, Icon: MessageSquare,
                title: 'Get Actionable Feedback',
                desc: 'Receive a detailed feedback report with strengths, areas for improvement, and specific next steps — reviewed by your interviewer before delivery.',
              },
            ].map(({ n, Icon, title, desc }) => (
              <div key={n} className="step-card">
                <div className="step-number">{n}</div>
                <div className="step-icon"><Icon size={32} /></div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" className="features-section">
        <div className="landing-container">
          <div className="section-header">
            <h2>Why Interview Dress Rehearsal?</h2>
            <p>Everything you need to walk in confident</p>
          </div>
          <div className="features-grid">
            {[
              { Icon: Target,       title: 'Role-Tailored Questions',  desc: 'AI-powered question generation based on your target role, experience level, and the actual job description.' },
              { Icon: Users,        title: 'Expert Interviewers',       desc: 'Practice with experienced professionals who have conducted real interviews at top companies.' },
              { Icon: MessageSquare,title: 'Structured Feedback',       desc: 'Get clear strengths, opportunities, and practice actions — no confusing numeric scores, just guidance you can use.' },
              { Icon: Shield,       title: 'Identity Protection',       desc: 'Interviewer identities are protected so feedback stays honest and unbiased.' },
              { Icon: Zap,          title: 'AI-Assisted Insights',      desc: 'AI drafts your feedback from session recordings, then your interviewer reviews and personalizes it before delivery.' },
              { Icon: Clock,        title: 'Quick Turnaround',          desc: 'Receive your detailed feedback report within 24 hours of your session.' },
            ].map(({ Icon, title, desc }) => (
              <div key={title} className="feature-card">
                <div className="feature-icon-wrap"><Icon size={24} /></div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Interviewer CTA ── */}
      <section id="become-interviewer" className="interviewer-cta">
        <div className="landing-container">
          <div className="cta-card">
            <div className="cta-content">
              <h2>Become an Interviewer</h2>
              <p>
                Are you an experienced professional who loves mentoring? Join our team of expert
                interviewers. Set your own schedule, get paid for your expertise, and help
                candidates land their dream jobs.
              </p>
              <ul className="cta-benefits">
                <li><Star size={16} /> Flexible scheduling — you set your availability</li>
                <li><Star size={16} /> Competitive pay with tiered rates ($50–$100/hr)</li>
                <li><Star size={16} /> Make a real impact on candidates' careers</li>
              </ul>
              <button className="btn-cta btn-lg" onClick={() => navigate('/apply-interviewer')}>
                Apply Now <ArrowRight size={20} />
              </button>
            </div>
            <div className="cta-visual">
              {[
                { Icon: Star,  value: '4.9', label: 'Avg. Interviewer Rating' },
                { Icon: Users, value: '50+', label: 'Active Interviewers' },
              ].map(({ Icon, value, label }) => (
                <div key={label} className="cta-stat-card">
                  <div className="cta-stat-icon"><Icon size={24} /></div>
                  <div className="cta-stat-value">{value}</div>
                  <div className="cta-stat-label">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="final-cta">
        <div className="landing-container">
          <h2>Ready to ace your next interview?</h2>
          <p>Schedule a dress rehearsal and walk in with confidence.</p>
          <div className="final-actions">
            <button className="btn-cta btn-lg" onClick={() => navigate('/signup')}>
              Schedule a Session <ArrowRight size={20} />
            </button>
            <button className="btn-ghost-dark btn-lg" onClick={() => navigate('/signin')}>
              Sign In
            </button>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="landing-footer">
        <div className="landing-container">
          <div className="footer-content">
            <div className="footer-brand">
              <img src="/InterviewDressRehearsal_LOGO.png" alt="IDR" className="footer-logo" />
              <span>Interview Dress Rehearsal</span>
            </div>
            <div className="footer-links">
              <a href="#how-it-works" onClick={(e) => { e.preventDefault(); scrollTo('how-it-works') }}>How It Works</a>
              <a href="#features"     onClick={(e) => { e.preventDefault(); scrollTo('features')     }}>Features</a>
              <a href="#become-interviewer" onClick={(e) => { e.preventDefault(); scrollTo('become-interviewer') }}>Become an Interviewer</a>
              <a href="/signin"  onClick={(e) => { e.preventDefault(); navigate('/signin') }}>Sign In</a>
            </div>
            <p className="footer-copy">&copy; {new Date().getFullYear()} Interview Dress Rehearsal. All rights reserved.</p>
          </div>
        </div>
      </footer>

    </div>
  )
}
